import { useState } from "react";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { FileText, FlaskConical, Info, LogOut, Mail, Scale, Settings as SettingsIcon, Star } from "lucide-react";
import Capture from "./routes/Capture";
import History from "./routes/History";
import Settings from "./routes/Settings";
import Favorites from "./routes/Favorites";
import Weight from "./routes/Weight";
import EditMeal from "./routes/EditMeal";
import MealDetail from "./routes/MealDetail";
import Nav from "./components/Nav";
import AuthGate from "./components/AuthGate";
import OnboardingGate from "./components/OnboardingGate";
import About from "./routes/About";
import CreateFavorite from "./routes/CreateFavorite";
import Legal from "./routes/Legal";
import Nutrients from "./routes/Nutrients";
import Onboarding from "./routes/Onboarding";
import Partners from "./routes/Partners";
import PartnerDay from "./routes/PartnerDay";
import AcceptShare from "./routes/AcceptShare";
import Admin from "./routes/Admin";
import FeedbackModal from "./components/FeedbackModal";
import { useAuth } from "./lib/auth";

declare const __BUILD_ID__: string;

export default function App() {
  const [navHidden, setNavHidden] = useState(false);
  const [stageLabel, setStageLabel] = useState("capture");
  return (
    <AuthGate>
      <OnboardingGate>
        <div className="min-h-full flex flex-col max-w-md md:max-w-2xl lg:max-w-3xl mx-auto">
          <TopBar />
          <main className="flex-1 pb-20">
            <Routes>
              <Route
                path="/"
                element={<Capture onReviewing={setNavHidden} onStageChange={setStageLabel} />}
              />
              <Route path="/history" element={<History />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/favorites" element={<Favorites />} />
              <Route path="/favorites/new" element={<CreateFavorite />} />
              <Route path="/weight" element={<Weight />} />
              <Route path="/about" element={<About />} />
              {/* Recompute-goals flow — re-runs the wizard from Settings */}
              <Route
                path="/onboarding"
                element={<OnboardingRecomputeRoute />}
              />
              <Route path="/nutrients" element={<Nutrients />} />
              <Route path="/partners" element={<Partners />} />
              {/* The /share/ route must come BEFORE the catch-all
                  /:partnerId so React Router matches the literal segment
                  first. Otherwise "share" gets read as a partner ID. */}
              <Route path="/partners/share/:shareId" element={<AcceptShare />} />
              <Route path="/partners/:partnerId" element={<PartnerDay />} />
              <Route path="/admin" element={<Admin />} />
              <Route path="/legal" element={<Legal />} />
              <Route path="/legal/:doc" element={<Legal />} />
              <Route path="/edit/:id" element={<EditMeal />} />
              <Route path="/meal/:id" element={<MealDetail />} />
            </Routes>
          </main>
          <Nav hidden={navHidden} />
          {import.meta.env.DEV && (
            <div className="fixed top-1 right-2 text-[10px] text-slate-700 font-mono pointer-events-none select-none">
              {__BUILD_ID__} · {stageLabel}
            </div>
          )}
        </div>
      </OnboardingGate>
    </AuthGate>
  );
}

function TopBar() {
  const { session, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  if (!session) return null;
  const email = session.user.email ?? "user";
  const initial = (email.match(/[a-zA-Z0-9]/)?.[0] ?? "?").toUpperCase();
  const title = topLevelTitle(location.pathname);
  return (
    <>
      <header className="px-4 pt-3 pb-1 flex items-center justify-between min-h-[44px]">
        {title ? (
          <h1 className="text-2xl font-bold text-slate-100 tracking-tight">{title}</h1>
        ) : (
          <span />
        )}
        <div className="relative">
          <button
            onClick={() => setOpen((v) => !v)}
            className="w-9 h-9 rounded-full bg-emerald-500 text-slate-950 font-semibold text-sm flex items-center justify-center hover:opacity-90 active:opacity-80 shadow-sm"
            aria-label="Account menu"
          >
            {initial}
          </button>
          {open && (
            <>
              {/* Click-outside catcher */}
              <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
              <div className="absolute top-11 right-0 bg-slate-900 border border-slate-800 rounded-lg shadow-xl overflow-hidden z-50 min-w-52">
                <div className="px-4 py-2.5 border-b border-slate-800 text-xs text-slate-500 truncate">
                  {email}
                </div>
                {[
                  { label: "Favorites", path: "/favorites", Icon: Star },
                  { label: "Nutrients", path: "/nutrients", Icon: FlaskConical },
                  { label: "Weight", path: "/weight", Icon: Scale },
                  { label: "Settings", path: "/settings", Icon: SettingsIcon },
                  { label: "About", path: "/about", Icon: Info },
                  { label: "Privacy & Terms", path: "/legal", Icon: FileText }
                ].map(({ label, path, Icon }) => (
                  <button
                    key={path}
                    onClick={() => {
                      setOpen(false);
                      navigate(path);
                    }}
                    className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-slate-200 hover:bg-slate-800 w-full text-left"
                  >
                    <Icon className="w-4 h-4 text-slate-400" strokeWidth={2} />
                    {label}
                  </button>
                ))}
                {/* Feedback sits between navigation + sign out — easy to find,
                    one tap from anywhere in the app. */}
                <button
                  onClick={() => {
                    setOpen(false);
                    setFeedbackOpen(true);
                  }}
                  className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-emerald-400 hover:bg-slate-800 w-full text-left border-t border-slate-800"
                >
                  <Mail className="w-4 h-4" strokeWidth={2} />
                  Send feedback
                </button>
                <button
                  onClick={() => {
                    setOpen(false);
                    signOut();
                  }}
                  className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-slate-200 hover:bg-slate-800 w-full text-left border-t border-slate-800"
                >
                  <LogOut className="w-4 h-4 text-slate-400" strokeWidth={2} />
                  Sign out
                </button>
              </div>
            </>
          )}
        </div>
      </header>
      {feedbackOpen && <FeedbackModal onClose={() => setFeedbackOpen(false)} />}
    </>
  );
}

/**
 * Wraps the Onboarding component for the recompute flow (Settings → Recompute).
 * On complete/skip, navigates back to home rather than relying on the gate.
 */
function OnboardingRecomputeRoute() {
  const navigate = useNavigate();
  return (
    <Onboarding
      onComplete={() => navigate("/")}
      onSkip={() => navigate("/")}
    />
  );
}

function topLevelTitle(path: string): string {
  if (path === "/") return "Today";
  if (path === "/history") return "History";
  // Partners has its own h1 inside the route, no top-bar title needed
  return "";
}
